/* eslint-disable @typescript-eslint/member-ordering */
import { Inject, Injectable } from '@angular/core';
import { Result } from '../models';
import { vendorManagement } from '../models/vendorManagement.queries';
import { ENV, IEnvironment } from 'env/config';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

import { Service } from '../entity';

import { DataService } from '../http/dataService';
import { CentralApis } from '../interfaces/internal';
import { ITerritory, Territory } from '../models/territory.model';
import { Fsa } from '../models/fsa.model';
import { VendorGroup } from '../models/vendorGroup.model';
import { Repository } from './repository';
import { RepRosterService } from './repRoster';
import { FilterServiceModel } from '../models/filterServiceModel';
import { calculateOverAllProgress } from '@telus/utils/utils';

@Service({
  api: 'territory',
  model: Territory,
})
@Injectable({ providedIn: 'root' })
export class TerritoryService extends Repository<Territory> {
  private vendorGroupService: Repository<VendorGroup>;

  private filterSource = new BehaviorSubject({});
  displayFilter = this.filterSource.asObservable();

  private applyFilterSource = new BehaviorSubject(false);
  applyFilter = this.applyFilterSource.asObservable();

  constructor(@Inject(ENV) env: IEnvironment, dataService: DataService) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).territory,
    });

    this.vendorGroupService = new Repository<VendorGroup>({
      client: dataService,
      url: env.api.backend.reproster.endpoint as string,
    });
    this.vendorGroupService.api = '';
  }

  static getParams(queryParams?, conditions?) {
    let filter = queryParams || {};

    if (conditions && conditions.condition.indexOf('vendorGroupId') !== -1) {
      filter = {
        ...queryParams,
        vendorGroups: conditions.value?.toString(),
      };
    } else if (
      conditions &&
      conditions.condition.indexOf('vendorAccountName') !== -1
    ) {
      filter = {
        ...queryParams,
        vendorAccountNames: conditions.value?.toString(),
      };
    }

    return filter;
  }

  getAll(queryParams?: any, conditions?) {
    const vendorGroupParams = TerritoryService.getParams(
      queryParams,
      conditions
    );

    return super.getAll(vendorGroupParams).pipe(
      switchMap((result) => {
        const territories = result.items;

        const createdBy = territories.map(
          (territory) => `'${territory.createdBy}'`
        );
        const modifiedBy = territories.map(
          (territory) => `'${territory.modifiedBy}'`
        );
        // getting unique IDs
        const federationId = [
          ...new Set(
            createdBy.concat(modifiedBy).filter((item) => item != null)
          ),
        ];

        // getting unique IDs
        const vendorGroupIds = [
          ...new Set(
            territories
              .map((territory) => `'${territory.vendorGroup}'`)
              .filter((territory) => territory != null)
          ),
        ];

        const vendorIdQuery = {
          q: RepRosterService.getVendorDropdownQuery({
            ids: vendorGroupIds.join(),
          }),
        };

        const federationIdQuery = {
          q: vendorManagement.vendorSalesRep.byCpmsId(federationId.join()),
        };

        const emptyResult: Result<any> = {
          items: [],
          links: null,
          meta: null,
        };

        return forkJoin([
          of(result),
          federationId.length === 0
            ? of(emptyResult)
            : this.vendorGroupService.getAll(federationIdQuery),
          vendorGroupIds.length === 0
            ? of(emptyResult)
            : this.vendorGroupService.getAll(vendorIdQuery),
        ]);
      }),
      map((data) => {
        const territoriesResult = data[0];
        const vendorFederationNames = data[1].items;
        const vendorNames = data[2].items;

        territoriesResult.items = territoriesResult.items.map((territory) =>
          Territory.completeTerritoryInformation(
            territory,
            vendorFederationNames,
            vendorNames
          )
        );

        return territoriesResult;
      })
    );
  }

  addFsa(queryParams) {
    const { territoryId, fsas } = queryParams;
    const originalApi = this.api;
    this.api = `${this.api}/${territoryId}/fsa`;

    return this.save(fsas).pipe(tap(() => (this.api = originalApi)));
  }

  removeFsa(queryParams) {
    const { territoryId, coid, fsa } = queryParams;
    const originalApi = this.api;
    this.api = `${this.api}/${territoryId}/fsa/${coid}/${fsa}`;

    return this.delete().pipe(tap(() => (this.api = originalApi)));
  }

  createFromDTO(item: ITerritory): Territory {
    return Territory.createFromDTO(item);
  }

  completeTerritory(territoryId) {
    return this.dataService
      .put<any>(`${this.url}${this.api}/${territoryId}/complete`, null)
      .pipe(take(1));
  }

  closeTerritory(territoryId) {
    return this.dataService
      .delete<any>(`${this.url}${this.api}/${territoryId}`)
      .pipe(take(1));
  }

  getOverallTerritoryProgress(territoryId) {
    return this.dataService
      .get<any>(`${this.url}${this.api}/${territoryId}/progress`)
      .pipe(
        take(1),
        map((result) => result?.items || [])
      );
  }

  getProgressList<I>(ids: string, territories: any) {
    return this.dataService
      .get<any>(`${this.url}${this.api}/progress?ids=${ids}`)
      .pipe(
        take(1),
        map((result) => ({
          meta: territories.meta,
          items: territories.items.map((item) => ({
            ...item,
            progress: this.getProgress(result, item.id) + '%',
          })),
        }))
      );
  }

  getProgress(result, itemid) {
    const territory = result.find((t) => t.territoryid === itemid);
    return territory
      ? calculateOverAllProgress(territory.totalstops, territory.knockedstops)
      : 0;
  }

  getFiltersByPolygon(polygon: any) {
    return this.dataService
      .post<any>(`${this.url}${this.api}/filters`, {
        type: 'Polygon',
        coordinates: polygon,
      })
      .pipe(take(1));
  }

  changeDisplayFilter(displayFilter: FilterServiceModel) {
    this.filterSource.next(displayFilter);
  }
}
